import { generatePath, useNavigate, useParams } from 'react-router-dom';

import styled from 'styled-components';

import { Badge } from '@components/badge';
import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { RouterTabsHeader } from '@components/header/router-tabs-header';
import { IconButton } from '@components/icon-button';
import { COLOR_TO_VARIANT, ICON_CHOOSER_ICONS } from '@components/icon-chooser/icon-chooser';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { BaseIcon } from '@components/icons/base';
import { IconBuilder } from '@components/icons/builder';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconEdit } from '@components/icons/edit';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconIssues } from '@components/icons/issues';
import { IconLoop } from '@components/icons/loop';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconPause } from '@components/icons/pause';
import { IconPlay } from '@components/icons/play';
import { IconRemove } from '@components/icons/remove';
import { IconResume } from '@components/icons/resume';
import { IconWorkspace } from '@components/icons/workspace';
import { Menu } from '@components/menu';
import { PageContent } from '@components/page-containers/page-container';
import { useRotatingLoadingMessages } from '@components/rotating-loading-messages';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useKeyboardShortcut } from '@hooks/use-keyboard-shortcut';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useIsOutOfCredits } from 'api/authentication/hooks';
import {
  useJourneyTraveller,
  useJourneyTravellerIssues,
  usePauseJourneyTraveller,
  useResetJourneyTraveller,
  useResumeJourneyTraveller,
  useStartJourneyTraveller,
} from 'api/traveller-journeys/hooks';
import { TravellerJourneyDetails } from 'api/traveller-journeys/types';
import { usePermissions } from 'contexts/permissions-context';
import { JourneyActivityTimeline } from 'ui/actions/activity-timeline';
import { routePaths } from 'ui/authenticated/navigation';

import { useCompanyTravellerToJourneySidebar } from './company-traveller-to-journey';
import {
  getSingleTravellerStatisticsData,
  useDeleteTravellerModal,
  useResetTravellerModal,
} from './list';
import { useTravellerDetailsSidebar } from './traveller-details-sidebar';
import { useTravellerIssuesSidebar } from './traveller-issues-sidebar';
import { TravellerJourney } from './traveller-journey';

import { useCurrentWorkspace } from '../../helpers';
import { PageNotFound } from '../page-not-found';

export const TravellerPage = () => {
  const { travellerId } = useParams();
  const { t } = useScopedTranslate(locale);
  const workspace = useCurrentWorkspace();
  const Icon = ICON_CHOOSER_ICONS[workspace.icon] || IconWorkspace;

  const { isError: isTravellerError, data: travellerQueryData } = useJourneyTraveller(
    travellerId ?? ''
  );
  const traveller = travellerQueryData;

  useRotatingLoadingMessages({
    isEnabled: Boolean(traveller?.isActionsUpdating),
    title: t('loading'),
    messages: [t('recreatingActions')],
  });

  if (isTravellerError) {
    return <PageNotFound message={t('notFoundMessage')} />;
  }
  if (traveller?.isActionsUpdating) {
    return null;
  }

  return traveller ? (
    <RouterTabsHeader
      breadcrumbs={<TravellerCrumbs traveller={traveller} />}
      tabs={[
        {
          label: t('travellerJourney'),
          section: 'travellerjourney',
          content: <TravellerJourney traveller={traveller} />,
        },
        {
          label: t('activity'),
          section: 'activity',
          content: (
            <PageContent noPadding>
              <ActivityContainer>
                <JourneyActivityTimeline
                  journeyId={traveller.shortId}
                  source={traveller.source}
                  sourceDescription={traveller.sourceDescription}
                  sourceData={traveller.sourceData}
                  dateCreated={traveller.dateCreated}
                />
              </ActivityContainer>
            </PageContent>
          ),
        },
      ]}
      actions={<TravellerActions traveller={traveller} />}
      backAction={
        <IconContainer
          hasBorder
          icon={<Icon color={workspace.color} />}
          variant={COLOR_TO_VARIANT[workspace.color]}
        />
      }
    />
  ) : null;
};

const ActivityContainer = styled.div({
  width: pxToRem(640),
  margin: `${pxToRem(32)} auto`,
  padding: `0 ${pxToRem(24)}`,
});

const IconContainer = styled(IconFancyContainer)({
  borderWidth: pxToRem(1),
});

const TravellerCrumbs = ({ traveller }: { traveller: TravellerJourneyDetails }) => {
  const { t } = useScopedTranslate(locale);

  const journeyPath = generatePath(routePaths.journeyDetails, {
    workspaceId: traveller.journey.workspace,
    journeyId: traveller.journey.shortId,
    '*': '',
  });
  const items = [
    { label: '...' },
    {
      label: t('journeys'),
      link: generatePath(routePaths.workspaceJourneyList, {
        workspaceId: traveller.journey.workspace,
      }),
    },
    { label: traveller.journey.title, link: journeyPath },
    { label: traveller.name, avatar: { name: traveller.name, src: traveller.picture } },
  ];
  return <Breadcrumb items={items} />;
};

const TravellerActions = ({ traveller }: { traveller: TravellerJourneyDetails }) => {
  const { t } = useScopedTranslate(locale);
  const navigate = useNavigate();
  const isOutOfCredits = useIsOutOfCredits();
  const { canManageJourneyTravellers, canEditJourneyTravellerData } = usePermissions();
  const showEditTravellerSidebar = useCompanyTravellerToJourneySidebar();
  const showTravellerDetailsSidebar = useTravellerDetailsSidebar();
  const data = { travellerJourneyId: traveller.shortId, journeyId: traveller.journey.shortId };
  const travellerIssuesQuery = useJourneyTravellerIssues(traveller.shortId);
  const startJourneyTravellerMutation = useStartJourneyTraveller(data);
  const pauseJourneyTravellerMutation = usePauseJourneyTraveller(data);
  const resumeJourneyTravellerMutation = useResumeJourneyTraveller(data);
  const onSuccess = async () => {
    navigate(
      generatePath(routePaths.journeyDetails, {
        workspaceId: traveller.journey.workspace,
        journeyId: traveller.journey.shortId,
        '*': '',
      })
    );
  };
  const warnings = travellerIssuesQuery.data?.warnings ?? [];
  const showIssueSidebar = useTravellerIssuesSidebar();
  const errors = travellerIssuesQuery.data?.errors ?? [];
  const statisticsData = getSingleTravellerStatisticsData(traveller.status);
  const showDeleteModal = useDeleteTravellerModal({ data, statisticsData, onSuccess });
  const showResetModal = useResetTravellerModal({ data, statisticsData });
  const resetTravellerMutation = useResetJourneyTraveller(data);
  useKeyboardShortcut({
    shortcut: 'meta+ctrl+r',
    onSuccess: () => resetTravellerMutation.mutate({}),
    requiresSecret: 'has-keyboard-shortcuts-enabled',
  });
  useKeyboardShortcut({
    shortcut: 'meta+ctrl+s',
    onSuccess: () => startJourneyTravellerMutation.mutate({}),
    requiresSecret: 'has-keyboard-shortcuts-enabled',
  });

  const { companyTravellerShortId, shortId, journey } = traveller;
  const { shortId: journeyShortId } = journey;
  const hasIssues = errors.length > 0 || warnings.length > 0;
  const finalStatus =
    isOutOfCredits && traveller.status === 'in_progress' ? 'paused' : traveller.status;

  return (
    <Stack space={2} orientation="horizontal">
      <Stack orientation="horizontal">
        {hasIssues && (
          <IssuesButton
            onClick={() =>
              showIssueSidebar({
                warnings,
                errors,
                traveller: {
                  companyTravellerShortId,
                  journeyShortId,
                  shortId,
                },
              })
            }
          >
            <IconIssues color="buttonIconPositive" />
            <Text color="textSecondary" font="semibold14" text={'Issues'} />
            <Stack orientation="horizontal" style={{ gap: theme.space[1] }}>
              {errors.length > 0 && (
                <IssueBadge
                  key={'error'}
                  Icon={IconFormIndicatorError}
                  text={errors.length.toString()}
                />
              )}
              {warnings.length > 0 && (
                <IssueBadge
                  key={'warning'}
                  Icon={IconFormIndicatorWarning}
                  text={warnings.length.toString()}
                />
              )}
            </Stack>
          </IssuesButton>
        )}
      </Stack>
      <Button
        variant="secondary"
        text={t('details')}
        onClick={() => {
          showTravellerDetailsSidebar({
            travellerId: traveller.shortId,
            journeyId: traveller.journey.shortId,
          });
        }}
      />
      <Menu
        dataTestid="traveller-journey-actions"
        buttonProps={{
          variant: 'secondary',
          iconRight: <IconMoreVertical />,
        }}
      >
        {canEditJourneyTravellerData && (
          <Menu.Item
            onSelect={() =>
              showEditTravellerSidebar({
                journeyShortId: traveller.journey.shortId,
                journeyTravellerId: traveller.shortId,
              })
            }
            label={t('editTraveller')}
            icon={<IconEdit />}
          />
        )}
        <Menu.Item
          onSelect={() =>
            navigate(
              generatePath(routePaths.editJourney, {
                workspaceId: traveller.journey.workspace,
                journeyId: traveller.journey.shortId,
                '*': '',
              })
            )
          }
          label={t('openJourneyBuilder')}
          icon={<IconBuilder />}
        />
        <Menu.Separator />
        {finalStatus === 'not_started' && (
          <Menu.Item
            onSelect={() => startJourneyTravellerMutation.mutate({})}
            label={t('startJourney')}
            icon={<IconPlay />}
            isDisabled={isOutOfCredits}
          />
        )}
        {finalStatus === 'in_progress' && (
          <Menu.Item
            onSelect={() => pauseJourneyTravellerMutation.mutate({})}
            label={t('pauseJourney')}
            icon={<IconPause />}
          />
        )}
        {finalStatus === 'paused' && (
          <Menu.Item
            onSelect={() => resumeJourneyTravellerMutation.mutate({})}
            label={t('resumeJourney')}
            icon={<IconResume />}
            isDisabled={isOutOfCredits}
          />
        )}
        <Menu.Item
          isDisabled={isOutOfCredits || finalStatus === 'not_started'}
          onSelect={showResetModal}
          label={t('resetJourney')}
          icon={<IconLoop />}
        />
        {canManageJourneyTravellers && (
          <>
            <Menu.Separator />
            <Menu.Item
              isDisabled={isOutOfCredits}
              onSelect={showDeleteModal}
              variant="danger"
              label={t('deleteTraveller')}
              icon={<IconRemove />}
            />
          </>
        )}
      </Menu>
      <IconButton
        icon={<IconCloseRegular />}
        onClick={() =>
          navigate(
            generatePath(routePaths.journeyDetails, {
              workspaceId: traveller.journey.workspace,
              journeyId: traveller.journey.shortId,
              '*': '',
            })
          )
        }
      />
    </Stack>
  );
};

export const IssueBadge = ({ Icon, text }: { Icon: typeof BaseIcon; text: string }) => {
  return <Badge size="md" bg="uiTrans5" icon={<Icon size={16} />} text={text} />;
};

export const IssueContainerStyled = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: theme.space[2],
  padding: theme.space[1],
});

const IssuesButton = styled.div({
  cursor: 'pointer',
  height: pxToRem(40),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.space[1],
  padding: `0 ${theme.space[2]}`,
  borderRadius: theme.space[2],
  backgroundColor: theme.color.uiTrans5,
});

export const locale = {
  en: {
    details: 'Details',
    travellerJourney: 'Traveller Journey',
    activity: 'Activity',
    journey: 'Journey',
    journeys: 'Journeys',
    editTraveller: 'Edit traveller',
    openJourneyBuilder: 'Open Journey Builder',
    deleteTraveller: 'Remove from journey',
    startJourney: 'Start journey',
    pauseJourney: 'Pause journey',
    resumeJourney: 'Resume journey',
    resetJourney: 'Reset journey',
    resetJourneys: 'Reset journey/s',
    reset: 'Reset',
    areYouSureReset:
      'Are you sure you want to reset this travellers journey? All current progress will be lost and the journey will be reset to its initial state',
    deleteTravellers: 'Remove traveller/s',
    theFollowingTraveller:
      'the following traveller from this journey? All journey details and progress will be lost',
    notFoundMessage: 'Are you sure the traveller journey exists and you have access to it?',

    loading: 'Loading',
    aligningWorkflow: 'Aligning stars and workflows ⭐',
    workflowMagic: 'Doing some workflow magic, hang tight!',
    organizingChaos: 'Organizing chaos into order...',
    verifying: 'Verifying journey integrity...',
    workflowNearlyThere: 'Workflow nearly there, just a moment more...',
    ensuringSystemsGo: 'Ensuring all systems are a go...',
    almostThere: 'Almost there!',
    recreatingActions: 'Recreating actions for traveller journey...',
  },
};
